import dayjs from "dayjs";
import { FileText, Pencil, Trash2 } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { PurchaseOrder } from "~/types/purchasing";
import {
  CURRENCY_SYMBOLS,
  ORDER_TYPE_LABELS,
  PURCHASE_ORDER_STATUS_COLORS,
  PURCHASE_ORDER_STATUS_LABELS,
} from "~/types/purchasing";

interface PurchaseOrdersTableProps {
  orders: PurchaseOrder[];
  isLoading: boolean;
  onRowClick: (order: PurchaseOrder) => void;
  onViewPdf?: (order: PurchaseOrder) => void;
  onEdit?: (order: PurchaseOrder) => void;
  onDelete?: (order: PurchaseOrder) => void;
  /** Hide the Acciones column — read-only embedding (e.g. supplier detail). */
  readOnly?: boolean;
}

export function PurchaseOrdersTable({
  orders,
  isLoading,
  onRowClick,
  onViewPdf,
  onEdit,
  onDelete,
  readOnly = false,
}: PurchaseOrdersTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay órdenes de compra registradas
      </div>
    );
  }

  const formatCurrency = (value: number, currency: string) => {
    const symbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS];
    return `${symbol}${new Intl.NumberFormat("es-CL").format(value)}`;
  };

  // Calculate reception progress for an order
  const getReceptionProgress = (order: PurchaseOrder) => {
    if (!order.items || order.items.length === 0) {
      return { total: 0, received: 0, progress: 0 };
    }
    const total = order.items.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    const received = order.items.reduce(
      (sum, item) => sum + (item.received_quantity || 0),
      0
    );
    const progress = total > 0 ? (received / total) * 100 : 0;
    return { total, received, progress };
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>OC #</TableHead>
          <TableHead>Fecha</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Proveedor</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Recepción</TableHead>
          <TableHead>Estado</TableHead>
          {!readOnly && (
            <TableHead className="text-right">Acciones</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((order, index) => {
          const { total, received, progress } = getReceptionProgress(order);
          const isComplete = progress >= 100;

          return (
            <TableRow
              key={order.id}
              className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              onClick={() => onRowClick(order)}
            >
              <TableCell className="font-medium">
                {String(order.po_number).padStart(5, "0")}
              </TableCell>
              <TableCell>
                {dayjs(order.issue_date).format("DD/MM/YYYY")}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={
                    order.order_type === "products"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-amber-100 text-amber-800"
                  }
                >
                  {ORDER_TYPE_LABELS[order.order_type]}
                </Badge>
              </TableCell>
              <TableCell>{order.supplier?.name || "-"}</TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(order.total, order.currency)}
              </TableCell>
              <TableCell>
                {order.status !== "cancelled" ? (
                  <div className="flex min-w-[100px] items-center gap-2">
                    <Progress value={progress} className="h-2 flex-1" />
                    <span
                      className={`whitespace-nowrap font-mono text-xs ${
                        isComplete ? "text-green-600" : "text-muted-foreground"
                      }`}
                    >
                      {Math.round(progress)}%
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={PURCHASE_ORDER_STATUS_COLORS[order.status]}
                >
                  {PURCHASE_ORDER_STATUS_LABELS[order.status]}
                </Badge>
              </TableCell>
              {!readOnly && (
                <TableCell className="text-right">
                  <div
                    className="flex justify-end gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewPdf?.(order)}
                      title="Ver PDF"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    {order.status === "draft" && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit?.(order)}
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete?.(order)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
